import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Typography, Select, MenuItem, Paper, Snackbar, Alert, Button, Menu, Divider, Dialog as MuiDialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AuthService from './services/AuthService';
import baseService from './services/BaseService';
import usageService from './services/UsageService';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
} from 'recharts';

function Usage() {
  const { t } = useTranslation();
  const loginAs = AuthService.getLoginAs();

  const [bases, setBases] = useState([]);
  const [selectedBase, setSelectedBase] = useState('');

  const [rangeFrom, setRangeFrom] = useState(dayjs().subtract(30, 'day').startOf('day'));
  const [rangeTo, setRangeTo] = useState(dayjs().endOf('day'));
  const [rangeMenuEl, setRangeMenuEl] = useState(null);
  const [openCustom, setOpenCustom] = useState(false);
  const [tempFrom, setTempFrom] = useState(rangeFrom);
  const [tempTo, setTempTo] = useState(rangeTo);

  const [usageData, setUsageData] = useState({
    credit_usage: [],
    interactions: [],
    unique_users: [],
  });

  // Custom tooltip for credit usage chart to show formatted date
  const renderCreditTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const dateValue = label || (payload[0] && payload[0].payload && payload[0].payload.date);
    const formattedDate = dateValue ? dayjs(dateValue).format('YYYY-MM-DD') : '';

    const sortedPayload = [...payload].sort((a, b) => {
      const nameA = (a.name || a.dataKey || '').toString().toLowerCase();
      const nameB = (b.name || b.dataKey || '').toString().toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return (
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 3,
          border: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        {formattedDate && (
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {formattedDate}
          </Typography>
        )}
        {sortedPayload.map((entry) => (
          <Typography
            key={entry.dataKey}
            variant="body2"
            sx={{ color: entry.color }}
          >
            {`${entry.name || entry.dataKey} : ${entry.value}`}
          </Typography>
        ))}
      </Box>
    );
  };

  // Shared tooltip for time-series line charts (interactions, unique users)
  const renderLineTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const dateValue = label || (payload[0] && payload[0].payload && payload[0].payload.date);
    const formattedDate = dateValue ? dayjs(dateValue).format('YYYY-MM-DD') : '';

    const sortedPayload = [...payload].sort((a, b) => {
      const nameA = (a.name || a.dataKey || '').toString().toLowerCase();
      const nameB = (b.name || b.dataKey || '').toString().toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return (
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 3,
          border: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        {formattedDate && (
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {formattedDate}
          </Typography>
        )}
        {sortedPayload.map((entry) => (
          <Typography
            key={entry.dataKey}
            variant="body2"
            sx={{ color: entry.color }}
          >
            {`${entry.name || entry.dataKey} : ${entry.value}`}
          </Typography>
        ))}
      </Box>
    );
  };

  // Flatten credit_usage so that each "count" object becomes per-model fields for stacked bars
  const flattenedCreditUsage = (usageData.credit_usage || []).map((item) => {
    const counts = item.count && typeof item.count === 'object' ? item.count : {};
    // Start with date so XAxis logic keeps working
    const flattened = { date: item.date };
    Object.keys(counts).forEach((key) => {
      // Use the raw key as field name; Recharts will handle multiple Bars
      flattened[key] = counts[key];
    });
    return flattened;
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [loadingUsage, setLoadingUsage] = useState(false);

  const loginAsUri = loginAs ? `?loginAs=${loginAs}` : '';

  const fetchBases = async () => {
    try {
      const basesResponse = await baseService.getBases(loginAs);
      setBases(basesResponse || []);
      if (basesResponse && basesResponse.length > 0) {
        const firstPath = basesResponse[0].s3_path;
        setSelectedBase(firstPath);
      }
    } catch (e) {
      console.error('Error fetching bases', e);
      setSnackbarMessage('Failed to load knowledge bases');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchUsage = async (baseOverride) => {
    try {
      setLoadingUsage(true);
      const s3Path = baseOverride || selectedBase;
      if (!s3Path) return;
      const params = {};
      if (rangeFrom) params.start_date = rangeFrom.startOf('day').unix();
      if (rangeTo) params.end_date = rangeTo.endOf('day').unix();
      const data = await usageService.getUsage(s3Path, loginAsUri, params);
      setUsageData({
        credit_usage: (data.credit_usage || []).slice(),
        interactions: (data.interactions || []).slice(),
        unique_users: (data.unique_users || []).slice(),
      });
    } catch (e) {
      console.error('Error fetching usage', e);
      setSnackbarMessage('Failed to load usage');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingUsage(false);
    }
  };

  useEffect(() => {
    fetchBases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBase) {
      fetchUsage(selectedBase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBase, rangeFrom, rangeTo]);

  const handleBaseChange = (event) => {
    const value = event.target.value;
    setSelectedBase(value);
    fetchUsage(value);
  };


  return (
    <Box sx={{ p: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            {t('files.knowledgeBases')}:
          </Typography>
          <Select
            value={selectedBase}
            onChange={handleBaseChange}
            displayEmpty
            size="small"
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="" disabled>
              {t('files.selectBase')}
            </MenuItem>
            {bases.map((base) => (
              <MenuItem key={base.s3_path} value={base.s3_path}>
                {base.base_name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {/* Date range pill and shortcuts mirroring Conversations */}
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => setRangeMenuEl(e.currentTarget)}
            sx={{ textTransform: 'none', borderRadius: 3, mr: 1 }}
          >
            {`${rangeFrom.format('MMM D')} - ${rangeTo.format('MMM D, YYYY')}`}
          </Button>
          <Menu
            anchorEl={rangeMenuEl}
            open={Boolean(rangeMenuEl)}
            onClose={() => setRangeMenuEl(null)}
          >
            <MenuItem onClick={() => { setRangeFrom(dayjs().startOf('day')); setRangeTo(dayjs().endOf('day')); setRangeMenuEl(null); }}>
              {t('conversation.today','Today')}
            </MenuItem>
            <MenuItem onClick={() => { setRangeFrom(dayjs().subtract(1,'day').startOf('day')); setRangeTo(dayjs().subtract(1,'day').endOf('day')); setRangeMenuEl(null); }}>
              {t('conversation.yesterday','Yesterday')}
            </MenuItem>
            <MenuItem onClick={() => { setRangeFrom(dayjs().subtract(7,'day').startOf('day')); setRangeTo(dayjs().endOf('day')); setRangeMenuEl(null); }}>
              {t('conversation.last7','Last 7 days')}
            </MenuItem>
            <MenuItem onClick={() => { setRangeFrom(dayjs().subtract(14,'day').startOf('day')); setRangeTo(dayjs().endOf('day')); setRangeMenuEl(null); }}>
              {t('conversation.last14','Last 14 days')}
            </MenuItem>
            <MenuItem onClick={() => { setRangeFrom(dayjs().subtract(30,'day').startOf('day')); setRangeTo(dayjs().endOf('day')); setRangeMenuEl(null); }}>
              {t('conversation.last30','Last 30 days')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setTempFrom(rangeFrom); setTempTo(rangeTo); setOpenCustom(true); setRangeMenuEl(null); }}>
              {t('conversation.custom','Custom')}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
        {/* Credit Usage - Bar per day */}
        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('usage.creditUsage')}
          </Typography>
          <Box sx={{ width: '100%', height: 260 }}>
            {flattenedCreditUsage.length === 0 ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">{t('usage.noData', 'No data available')}</Typography>
              </Box>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  key={`credit-${rangeFrom.valueOf()}-${rangeTo.valueOf()}`}
                  data={flattenedCreditUsage}
                  margin={{ top: 8, right: 16, bottom: 16, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={(item) => (item.date ? dayjs(item.date).valueOf() : null)}
                    type="number"
                    domain={[rangeFrom.startOf('day').valueOf(), rangeTo.endOf('day').valueOf()]}
                    tickLine={false}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(ts) => dayjs(ts).format('MM/DD')}
                  />
                  <YAxis hide />
                  <Tooltip content={renderCreditTooltip} />
                  {Object.keys(flattenedCreditUsage[0] || {})
                    .filter((key) => key !== 'date')
                    .map((key, index) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="credit"
                        fill={['#1976d2', '#2e7d32', '#ffb300', '#9c27b0', '#ef5350', '#26c6da'][index % 6]}
                      />
                    ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>

        {/* Interactions - Line over time */}
        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('usage.interactions', 'Interactions')}
          </Typography>
          <Box sx={{ width: '100%', height: 260 }}>
            {usageData.interactions.length === 0 ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">{t('usage.noData', 'No data available')}</Typography>
              </Box>
            ) : (
              <ResponsiveContainer>
                <LineChart
                  key={`interactions-${rangeFrom.valueOf()}-${rangeTo.valueOf()}`}
                  data={usageData.interactions}
                  margin={{ top: 8, right: 16, bottom: 16, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={(item) => (item.date ? dayjs(item.date).valueOf() : null)}
                    type="number"
                    domain={[rangeFrom.startOf('day').valueOf(), rangeTo.endOf('day').valueOf()]}
                    tickLine={false}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(ts) => dayjs(ts).format('MM/DD')}
                  />
                  <YAxis hide />
                  <Tooltip content={renderLineTooltip} />
                  <Line type="monotone" dataKey="count" stroke="#2e7d32" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>

        {/* Unique Users - Line over time */}
        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('usage.uniqueUsers', 'Unique Users')}
          </Typography>
          <Box sx={{ width: '100%', height: 260 }}>
            {usageData.unique_users.length === 0 ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">{t('usage.noData', 'No data available')}</Typography>
              </Box>
            ) : (
              <ResponsiveContainer>
                <LineChart
                  key={`users-${rangeFrom.valueOf()}-${rangeTo.valueOf()}`}
                  data={usageData.unique_users}
                  margin={{ top: 8, right: 16, bottom: 16, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={(item) => (item.date ? dayjs(item.date).valueOf() : null)}
                    type="number"
                    domain={[rangeFrom.startOf('day').valueOf(), rangeTo.endOf('day').valueOf()]}
                    tickLine={false}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(ts) => dayjs(ts).format('MM/DD')}
                  />
                  <YAxis hide />
                  <Tooltip content={renderLineTooltip} />
                  <Line type="monotone" dataKey="count" stroke="#9c27b0" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Loading dialog while waiting for usage response - styled similar to Files/Conversations */}
      <MuiDialog
        open={loadingUsage}
        onClose={() => {}}
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            minWidth: 320,
            textAlign: 'center',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {t('usage.loadingTitle', 'Loading usage')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('usage.loadingMessage', 'Waiting for response...')}
          </Typography>
        </DialogContent>
      </MuiDialog>

      {/* Custom date range dialog */}
      <MuiDialog open={openCustom} onClose={() => setOpenCustom(false)}>
        <DialogTitle>{t('conversation.custom', 'Custom range')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              type="date"
              label={t('conversation.from', 'From')}
              InputLabelProps={{ shrink: true }}
              value={tempFrom.format('YYYY-MM-DD')}
              onChange={(e) => {
                const value = e.target.value;
                if (value) setTempFrom(dayjs(value).startOf('day'));
              }}
            />
            <TextField
              type="date"
              label={t('conversation.to', 'To')}
              InputLabelProps={{ shrink: true }}
              value={tempTo.format('YYYY-MM-DD')}
              onChange={(e) => {
                const value = e.target.value;
                if (value) setTempTo(dayjs(value).endOf('day'));
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCustom(false)}>
            {t('conversation.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setRangeFrom(tempFrom);
              setRangeTo(tempTo);
              setOpenCustom(false);
            }}
          >
            {t('conversation.apply', 'Apply')}
          </Button>
        </DialogActions>
      </MuiDialog>
    </Box>
  );
}

export default Usage;
